import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Flex,
  Tag,
} from '@bridebook/aura';
import { useTranslation } from 'next-i18next';

import { IWeddingCampaign } from '@bridebook/models/source/models/Weddings/Campaigns.types';
import {
  DATE_FORMAT,
  format,
} from '@bridebook/web/lib/time-date-manipulation/time-date-manipulation';
import { getWeddingWebsitePassword } from '@bridebook/web/lib/wedding-website/hooks/query/selectors';
import { useWeddingWebsite } from '@bridebook/web/lib/wedding-website/hooks/query/website/use-wedding-website';
import { useMemo } from 'react';
import { CampaignSummaryWebsiteSection } from './campaign-summary-website-section';
import { SummarySection, SummarySectionRegularText, SummarySectionTitle } from './summary-section';

interface IProps {
  recipientsAccordionDefaultOpen?: boolean;
  recipients: string[];
  subject: string;
  message: string;
  includeGuestsNames: boolean;
  guestName?: string;
  websiteUrl?: string;
  sentDate?: IWeddingCampaign['createdAt'];
  displayPassword?: boolean;
}

export const CampaignSummary = ({
  recipients,
  sentDate,
  subject,
  message,
  includeGuestsNames,
  guestName,
  websiteUrl,
  recipientsAccordionDefaultOpen = false,
  displayPassword = false,
}: IProps) => {
  const { t } = useTranslation('inbox');
  const recipientsAccordionValue = 'recipients';

  const guestNameToUse = guestName || t('guests.messageSummary.guestName');

  const sentDateFormatted = useMemo(() => {
    if (!sentDate) return '';
    return format(sentDate.toDate(), DATE_FORMAT.DATE.WITH_DAY);
  }, [sentDate]);

  const { data: weddingWebsitePassword } = useWeddingWebsite({
    select: getWeddingWebsitePassword,
    enabled: displayPassword,
  });

  return (
    <>
      <Accordion
        type="single"
        collapsible
        defaultValue={recipientsAccordionDefaultOpen ? recipientsAccordionValue : undefined}>
        <AccordionItem value={recipientsAccordionValue}>
          <AccordionTrigger>
            <SummarySectionTitle>{t('guests.messageSummary.recipients2')}</SummarySectionTitle>
          </AccordionTrigger>
          <AccordionContent>
            <Flex className="flex-col gap-4">
              <div className="flex flex-wrap gap-2">
                {recipients.map((email, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center rounded-sm bg-space-08 px-[6px] py-[1px] text-[10px] leading-[14px] tracking-[-0.2px] break-words break-all text-space">
                    {email}
                  </div>
                ))}
              </div>
              {sentDate && sentDateFormatted && (
                <div className="text-sm font-semibold text-space-60">
                  {t('guests.messageSummary.sentDateText2', {
                    sentDate: sentDateFormatted,
                  })}
                </div>
              )}
            </Flex>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <SummarySection>
        <SummarySectionTitle>{t('guests.messageSummary.emailSubject2')}</SummarySectionTitle>
        <SummarySectionRegularText>{subject}</SummarySectionRegularText>
      </SummarySection>
      <SummarySection>
        <SummarySectionTitle>{t('guests.messageSummary.message2')}</SummarySectionTitle>
        <SummarySectionRegularText>
          {includeGuestsNames && (
            <>
              <div className="inline-flex items-center gap-1">
                <Tag theme="indigoCrush10">{guestNameToUse}</Tag>
                <span>,</span>
              </div>
              <br />
              <br />
            </>
          )}
          {message}
        </SummarySectionRegularText>
      </SummarySection>
      {websiteUrl && <CampaignSummaryWebsiteSection websiteUrl={websiteUrl} />}
      {displayPassword && weddingWebsitePassword && (
        <SummarySection>
          <SummarySectionTitle>{t('guests.messageSummary.password')}</SummarySectionTitle>
          <SummarySectionRegularText>{weddingWebsitePassword}</SummarySectionRegularText>
        </SummarySection>
      )}
    </>
  );
};
